import { Box, TextInput } from "@mantine/core";
import { useState } from "react";
import { BasicConfirmationModal } from "../BasicConfirmationModal/BasicConfirmationModal";

interface MultiConfirmationModalProps {
  actionName: string;
  actionObjects: Array<string>;
  confirmationText: string;
  confirmAction?: boolean;
  headerText?: string;
  isModalOpen: boolean;
  matchTextToConfirmActionText?: React.ReactNode;
  objectName: string;
  onAction: () => Promise<void>;
  toggleModal: () => void;
}

/** A confirmation modal that automatically handles the confirmation and action
 * (e.g. deletion) of multiple objects.
 */
function MultiConfirmationModal({
  actionName,
  actionObjects,
  confirmationText,
  confirmAction,
  headerText,
  isModalOpen,
  matchTextToConfirmActionText,
  objectName,
  onAction,
  toggleModal
}: MultiConfirmationModalProps) {
  const [loading, setLoading] = useState(false);
  const [confirmationValue, setConfirmationValue] = useState("");

  function getCustomHeaderText() {
    if (headerText && headerText.length > 0) {
      return headerText;
    }

    if (actionObjects.length > 1) {
      return `${actionObjects.length} ${objectName} ${actionName}?`;
    }

    return `${objectName} ${actionName}?`;
  }

  function handleClickConfirm() {
    setLoading(true);
    toggleModal();
    onAction().finally(() => {
      setLoading(false);
    });
  }

  const customHeaderText = getCustomHeaderText();
  const capitalizedActionName =
    actionName.charAt(0).toUpperCase() + actionName.slice(1);
  const canClickConfirm =
    !confirmAction || confirmationValue === actionObjects[0];

  return (
    <BasicConfirmationModal
      confirmationButtonColor="error"
      confirmationButtonText={capitalizedActionName}
      isConfirmationBtnDisabled={!canClickConfirm}
      isLoading={loading}
      opened={isModalOpen}
      title={customHeaderText}
      toggleModal={toggleModal}
      onCancel={toggleModal}
      onConfirm={handleClickConfirm}
    >
      <p>{confirmationText}</p>
      <ul>
        {actionObjects.map((object, index) => (
          <li key={`object${index}`}>{object}</li>
        ))}
      </ul>
      {confirmAction && (
        <Box>
          <TextInput
            label={matchTextToConfirmActionText}
            value={confirmationValue}
            onChange={(e) => setConfirmationValue(e.currentTarget.value)}
          />
        </Box>
      )}
    </BasicConfirmationModal>
  );
}

export { MultiConfirmationModal, MultiConfirmationModalProps };
